import React from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import { Carousel, Image, LinkIcon, ResponsiveImage } from 'reaxl';
import { EmailCTA } from 'reaxl-email';

import MediaGalleryImage from './MediaGalleryImage';
import MediaSliderShowcase from './MediaSliderShowcase';

function MediaSlider({
    emailCTAProps,
    images = [],
    videos = [],
    listingTitle = 'Listing',
    onImageClick,
    onVideoClick,
    showSpin = false,
    hasFyusion = false,
    mediaType = 'md',
    vhrTile,
}) {
    const device = useDevice();

    const isSm = device.is.sm;
    const isMd = device.is.md;
    const isLg = device.is.lg;

    const fallbackImg = '/cm-api/content/static/img/placeholders/vin_placeholder.png';

    const createMediaArray = () => {
        const combinedMedia = [];

        // If 360 spin exists, add it first and use first image as its src
        // If listing has Spin360 but no images, use fallback placeholder as its src
        if (showSpin) {
            combinedMedia.push({
                type: 'spin',
                data: (images.length > 0 && images[0]?.src) ? images[0] : { src: fallbackImg },
                index: 0,
            });
        }

        // Add videos next (after spin or as first if no spin)
        videos.forEach((video, videoIndex) => {
            combinedMedia.push({
                type: 'video',
                data: video,
                videoIndex,
                index: combinedMedia.length,
            });
        });

        // Add all images
        images.forEach((image, imageIndex) => {
            combinedMedia.push({
                type: 'image',
                data: image,
                imageIndex,
                index: combinedMedia.length,
            });
        });

        // Add VHR tile if available
        if (vhrTile) {
            combinedMedia.push({
                type: 'vhr',
                isVhr: true,
                index: combinedMedia.length,
            });
        }

        // Add placeholder if only one image and no VHR
        if (!vhrTile && images.length === 1) {
            combinedMedia.push({
                type: 'placeholder',
                isPlaceholder: true,
                index: combinedMedia.length,
            });
        }

        // Add request button
        combinedMedia.push({
            type: 'requestButton',
            isRequestButton: true,
            index: combinedMedia.length,
        });

        return combinedMedia;
    };

    const mediaArray = createMediaArray();

    let carouselProps = {
        fullWidthScrollXs: true,
        initialIndex: 0,
        scrollItemsCount: 3, // 6 photos at a time
        visibilityThreshold: 0.9,
    };

    // Render 360 spin showcase
    const renderSpinShowcase = (data, key = 'spin-360') => (
        <MediaSliderShowcase
            key={key}
            type="spin"
            src={data.src}
            alt={`${listingTitle} 360° Tour`}
            mediaType={mediaType}
            hasFyusion={hasFyusion}
            listingTitle={listingTitle}
        />
    );

    // Render video showcase
    const renderVideoShowcase = (data, videoIndex, key) => (
        <MediaSliderShowcase
            key={key}
            type="video"
            src={images[videoIndex]?.src || images[0]?.src}
            videoThumbnail={data.thumbnail}
            alt={`${listingTitle} video ${videoIndex + 1}`}
            mediaType={mediaType}
            listingTitle={listingTitle}
            videoIndex={videoIndex}
            onClick={onVideoClick}
        />
    );

    // Render image
    const renderImageComponent = (data, imageIndex, key) => {
        const isFirstImageInArray = imageIndex === 0 && !showSpin && videos.length === 0;

        return (
            <MediaGalleryImage
                alt={`${listingTitle} image ${imageIndex + 1}`}
                isSlider
                key={key}
                lazyImage={isFirstImageInArray ? 'eager' : 'lazy'}
                fetchPriority={isFirstImageInArray ? 'high' : 'auto'}
                onClick={(e) => onImageClick(e, imageIndex)}
                src={data.src}
                type="main"
            />
        );
    };

    // Render AutoCheck VHR tile
    const renderAutoCheckVhr = () => (
        <div
            className="slider-button img-rounded flex-column"
            key="AutoCheckVhr"
        >
            <Image
                src={vhrTile?.image?.src}
                alt="Media Slider Autocheck VHR image"
                height="72px"
                lazyload
                loading="lazy"
                draggable={false}
            />
            <LinkIcon
                className="margin-top-2"
                onClick={() => window.open(vhrTile?.link?.href, '_blank')}
                rel="nofollow noopener"
                target="_blank"
                undecorated
            >
                View History Report
            </LinkIcon>
        </div>
    );

    // Render request button
    const renderRequestButton = (labelKey = 'morePhotos') => (
        <EmailCTA
            {...emailCTAProps}
            className="slider-button img-rounded"
            key="requestButton"
            showIcon={false}
            buttonStyle="link"
            context="mediaGallery"
            labelKey={labelKey}
        />
    );

    // Render placeholder image
    const renderImgPlaceholder = () => (
        <div
            style={{ position: 'relative' }}
            key="ImgPlaceholder"
        >
            <ResponsiveImage
                alt={`${listingTitle} image placeholder`}
                src={fallbackImg}
                lazyload={false}
                draggable={false}
            />
            <div className="placeholder-box-shadow img-rounded" />
        </div>
    );

    // Render all media types
    const renderMediaByType = (media, customKey = null) => {
        const { type, data, index, videoIndex, imageIndex } = media;
        const key = customKey || `${type}-${index}`;

        switch (type) {
            case 'spin':
                return renderSpinShowcase(data, key);
            case 'video':
                return renderVideoShowcase(data, videoIndex, key);
            case 'image':
                return renderImageComponent(data, imageIndex, key);
            case 'vhr':
                return renderAutoCheckVhr();
            case 'placeholder':
                return renderImgPlaceholder();
            case 'requestButton':
                return renderRequestButton(images?.length >= 10 ? 'moreInfo' : 'morePhotos');
            default:
                return null;
        }
    };

    // Xs and Sm Slider layout
    if (!(isMd || isLg)) {
        carouselProps = {
            ...carouselProps,
            className: !isSm ? 'row' : '',
            prevNextButtons: false,
            singleImageMode: true,
            scrollItemsCount: 1,
        };

        return (
            <Carousel
                {...carouselProps}
            >
                {mediaArray.map((media) => renderMediaByType(media))}
            </Carousel>
        );
    }

    // Md and Lg Slider layout
    const [firstMedia, ...restMedia] = mediaArray || [];

    // Group rest elements into pairs for two-column layout
    const columns = [];
    for (let i = 0; i < restMedia.length; i += 2) {
        columns.push(restMedia.slice(i, i + 2));
    }

    // Render the first media item (takes up two column spaces)
    const renderFirstMedia = () => {
        if (!firstMedia) return null;
        return renderMediaByType(firstMedia);
    };

    return (
        <Carousel
            {...carouselProps}
        >
            {firstMedia && (
                <div
                    key="First-Media"
                    className={clsx('margin-1 media-slider-first-image', { md: isMd, lg: isLg })}
                >
                    {renderFirstMedia()}
                </div>
            )}
            {/* FOR 6 PHOTOS SCROLLING: Spacer div to occupy one column, so that firstMedia visually takes up the space of two columns */}
            {firstMedia && <div key="First-Media-Spacer" />}
            {columns.map((pair, colIdx) => (
                <div
                    key={'Column ' + colIdx}
                    className={clsx('margin-left-1 media-slider-column', { md: isMd, lg: isLg })}
                >
                    {pair.map((element, idx) => (
                        <div
                            key={`Column-${colIdx}-Media-${idx}`}
                            className="padding-1"
                        >
                            {renderMediaByType(element, `${element.type}-${element.index}`)}
                        </div>
                    ))}
                </div>
            ))}
        </Carousel>
    );
}

MediaSlider.propTypes = {
    emailCTAProps: PropTypes.object,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
        })
    ).isRequired,
    videos: PropTypes.arrayOf(
        PropTypes.shape({
            thumbnail: PropTypes.string,
            videoId: PropTypes.string,
        })
    ),
    listingTitle: PropTypes.string,
    onImageClick: PropTypes.func.isRequired,
    onVideoClick: PropTypes.func,
    showSpin: PropTypes.bool,
    hasFyusion: PropTypes.bool,
    mediaType: PropTypes.string,
    vhrTile: PropTypes.object,
};

export default React.memo(MediaSlider);
